/**
 * How an exposed fastener metal roof is finished, asked before it is generated.
 *
 * An AG panel or R panel roof is two decisions before it is any quantities.
 * The panel is painted or it is bare galvalume, and that runs through the whole
 * material order rather than one line — every piece of trim, the ridge cap, the
 * stitch screws and the touch-up paint are all one or the other, and a roof
 * that mixes them is a roof somebody has to sort out on the day. And the panel
 * either goes on furring strips over the old roof or straight onto the deck,
 * which is three lines of material and labour and a different length of screw.
 *
 * Jonathon, 2026-09-09: "it's easier on exposed metal templates before it
 * creates them to ask the user if furring strips are needed, and if it's going
 * to be painted or galvalume. whatever the rep selects is how the rest of the
 * relevant areas are priced and written... if someone selects galvalume then
 * everything is galvalume in the material order. if someone selects painted
 * then everything is painted."
 *
 * The two answers work differently on purpose:
 *
 *   - Furring strips **select lines**, through `applies_when` and `lineApplies`
 *     in flatRoof.ts. The lines are already on the template; the answer decides
 *     which of them are written. This is the same mechanism a flat roof uses.
 *
 *   - The finish **swaps products**, through `galvalume_twin_id`. It cannot be
 *     line selection, because that would mean carrying every trim twice on
 *     every template and keeping the two halves in step forever. A twin is a
 *     fact about the product — the same part in a different coating — so it
 *     lives on the product and one template covers both finishes.
 *
 * Nothing here touches money. The swapped product carries its own cost and the
 * ladder does the arithmetic; this module answers "which lines, and which part".
 * `linesToPlan` in templateLines.ts is where both answers are applied, for the
 * estimate builder and for an order from measurements alike.
 */

/**
 * The roof types that are an exposed fastener metal roof.
 *
 * Three strings rather than one because the catalogue grew that way: the AG and
 * R panel residential templates were given their own roof types when the panel
 * work was pointed at the real ETH roofing (20260809110000), and the generic
 * one is what the seed gave everything else. They are free text on the document
 * and are never renamed (CLAUDE.md rule 8), so the list is added to rather than
 * tidied.
 */
export const EXPOSED_FASTENER_ROOF_TYPES = [
  "Exposed Fastener Metal Panel",
  "AG Panel 26 Gauge Residential",
  "R Panel 26 Gauge Residential",
] as const;

export const isExposedFastenerType = (
  roofType: string | null | undefined,
): boolean =>
  EXPOSED_FASTENER_ROOF_TYPES.includes(
    (roofType ?? "").trim() as (typeof EXPOSED_FASTENER_ROOF_TYPES)[number],
  );

/** Painted, or bare galvalume. */
export const PANEL_FINISHES = ["painted", "galvalume"] as const;
export type PanelFinish = (typeof PANEL_FINISHES)[number];

/** What to call each on screen and in the scope of work. */
export const FINISH_LABELS: Record<PanelFinish, string> = {
  painted: "Painted",
  galvalume: "Galvalume",
};

/** Every answer the builder collects before it generates a metal panel roof. */
export interface ExposedFastenerAnswers {
  finish: PanelFinish;
  /**
   * Whether the panel goes on furring strips.
   *
   * "furring strips involve furring strip labor, furring strips, and framing
   * nails. so if they don't select those then you dont need to generate those
   * items." The screw changes too — 2" through a 1x4 into the deck, 1 1/2"
   * when the panel lands on the deck itself.
   */
  furringStrips: boolean;
}

/**
 * What the builder starts with: painted, on furring strips.
 *
 * The commonest job by a distance — furring strips were on every AG panel roof
 * by standing instruction until this question existed, and painted is what all
 * three templates are named for. So it is the answer that needs no clicks.
 */
export function defaultExposedFastenerAnswers(): ExposedFastenerAnswers {
  return { finish: "painted", furringStrips: true };
}

/**
 * The tokens a set of answers satisfies.
 *
 * Read by `lineApplies`, which is shared with the flat roof questions. A line
 * with no `applies_when` is on every metal panel roof; `furring` is on the ones
 * that have strips, `!furring` on the ones that do not, and `finish:painted` is
 * how the two paint lines stay off a galvalume order.
 */
export function exposedFastenerTokens(
  answers: ExposedFastenerAnswers,
): ReadonlySet<string> {
  const tokens = new Set<string>([`finish:${answers.finish}`]);
  if (answers.furringStrips) tokens.add("furring");
  return tokens;
}

/** A template line, as far as the finish swap is concerned. */
export interface FinishSwappable {
  /** Nullable because `roof_template_items.product_id` is. */
  product_id: string | null;
  /**
   * The line's own wording, when it has one.
   *
   * Cleared by a swap. Every override on the three metal templates today is a
   * verbatim copy of the product name, so keeping one across a swap would print
   * "29ga Wide Ridge Cap 10'2" Painted" on a galvalume order — the one thing
   * this whole module exists to prevent.
   */
  name?: string | null;
}

/** What a finish swap did, and what it could not do. */
export interface FinishSwap<T> {
  items: T[];
  /**
   * Product ids that are painted parts with no galvalume twin.
   *
   * Reported rather than swallowed. A line with no twin stays painted, which is
   * wrong on a galvalume roof but visibly wrong — the alternative is dropping
   * it, and a part missing from the order gets bought on the day at whatever it
   * costs.
   *
   * Not empty on the templates as they stand, and not expected to be: the pairs
   * are named one by one in migrations (20260909130000, 20260910200000), and
   * the catalogue is edited in Tools between them. Anything colour-matched on a
   * metal panel template is marked painted whether or not its twin was found,
   * so the next part somebody adds without one lands here rather than on the
   * order unnoticed.
   */
  missing: string[];
}

/**
 * The same template lines in the finish that was asked for.
 *
 * A painted roof is the template as it stands, so this is a no-op there and the
 * items come back untouched. A galvalume roof swaps every line whose product
 * has a twin, and reports the painted parts that have none.
 */
export function applyFinish<T extends FinishSwappable>(
  items: readonly T[],
  finish: PanelFinish,
  index: FinishIndex,
): FinishSwap<T> {
  if (finish === "painted") return { items: [...items], missing: [] };

  const missing: string[] = [];
  const swapped = items.map((item) => {
    const id = item.product_id;
    if (id === null) return item;
    const twin = index.twinOf.get(id);
    if (twin === undefined || twin === id) {
      if (index.paintedWithoutTwin.has(id)) missing.push(id);
      return item;
    }
    return { ...item, product_id: twin, name: null };
  });
  return { items: swapped, missing };
}

/** A product, as far as the finish swap is concerned. */
export interface FinishTwinned {
  id: string;
  /**
   * Which finish this product is, or null when it does not have one.
   *
   * The distinction has to be recorded rather than inferred. Without it a foam
   * closure — the same part in either finish — is indistinguishable from a rake
   * trim whose galvalume twin nobody has entered yet, and the second of those
   * is the one worth warning about. Naming it after the coating would be the
   * obvious shortcut and is exactly the rename-sensitive matching rule 8 warns
   * off: "Painted" comes out of a product name the day somebody tidies one.
   */
  finish?: string | null;
  galvalume_twin_id?: string | null;
}

/** The catalogue's finish pairings, in the two shapes a swap needs. */
export interface FinishIndex {
  /** Painted product id to its galvalume twin. */
  twinOf: ReadonlyMap<string, string>;
  /** Painted products with no usable twin — a gap, not an absence. */
  paintedWithoutTwin: ReadonlySet<string>;
}

/**
 * Which product stands in for which, read off the catalogue.
 *
 * Built at generation rather than stored anywhere, so a twin entered in Tools
 * takes effect on the next estimate without a deploy.
 *
 * `products` is the catalogue the plan will be priced from — the active
 * products. A twin that is not among them has been deactivated (rule 8: a
 * product is retired, never renamed or deleted), and swapping to it would hand
 * `generatePlan` a line it cannot find, which it skips as removed. The part
 * would vanish from the order. So a twin only counts when it is in the list,
 * and a painted part whose twin is not is a gap like any other: it stays
 * painted and says so.
 */
export function finishIndex(products: readonly FinishTwinned[]): FinishIndex {
  const present = new Set(products.map((product) => product.id));
  const twinOf = new Map<string, string>();
  const paintedWithoutTwin = new Set<string>();
  for (const product of products) {
    const twin = product.galvalume_twin_id;
    if (twin && twin !== product.id && present.has(twin)) {
      twinOf.set(product.id, twin);
    } else if (twin || product.finish === "painted") {
      // A product with a twin is painted by construction — the database
      // refuses a twin on anything else (`products_galvalume_twin_sane`).
      paintedWithoutTwin.add(product.id);
    }
  }
  return { twinOf, paintedWithoutTwin };
}

/**
 * The product the customer's headline line names, in the finish chosen.
 *
 * Every metal panel template's headline is its painted panel, because that is
 * what the templates are written in. A galvalume estimate headed "AG Panel 26
 * Gauge (Color)" contradicts itself on the first line the customer reads, so
 * the headline goes through the same twin the material order does.
 */
export function headlineInFinish(
  headlineProductId: string | null | undefined,
  finish: PanelFinish,
  index: FinishIndex,
): string | null {
  if (!headlineProductId) return null;
  if (finish === "painted") return headlineProductId;
  return index.twinOf.get(headlineProductId) ?? headlineProductId;
}

/**
 * What the customer reads about the two answers.
 *
 * Written from the answers rather than stored on the template, for the reason
 * the flat roof scope is: the template describes every roof it can produce and
 * this estimate is one of them, so a stored paragraph would go on promising
 * furring strips to somebody who is not getting any.
 *
 * The finish first, the fastening second. `metalScopeNote` uses the first on
 * its own when the template already says how the panels are fastened.
 */
export function exposedFastenerScopeOfWork(
  answers: ExposedFastenerAnswers,
): string[] {
  const lines: string[] = [];
  lines.push(
    answers.finish === "galvalume"
      ? "All panels, trim, ridge cap and fasteners are bare Galvalume."
      : "All panels, trim, ridge cap and fasteners are color matched to the color you select.",
  );
  lines.push(
    answers.furringStrips
      ? "Install furring strips over the existing roof deck and fasten the new panels through them."
      : "Fasten the new panels directly to the roof deck.",
  );
  return lines;
}

/** The parts of a template `metalScopeNote` reads. */
export interface MetalScopeTemplate {
  scope?: string | null;
  scope_warranty_painted?: string | null;
  scope_warranty_galvalume?: string | null;
}

/**
 * The fastening sentence both stored metal scopes carry, in section 2.3.
 *
 * "Install the 26 gauge metal roofing panels with Lifetime fasteners directly
 * over the prepared decking." Written before furring strips were a question,
 * when the owner's scope described the one build it sold without them.
 */
const DIRECT_TO_DECK = /directly over the prepared decking/i;

/** What that sentence says instead when the panels go on furring strips. */
const OVER_FURRING = "over furring strips fastened to the prepared decking";

/** The heading of section 3, where the answers must not come after. */
const SECTION_THREE = /^\s*3\.\s/;

/**
 * Sections 1 to 3 with the two answers written into them.
 *
 * Appending the answers after the stored scope was wrong twice over. On the
 * default roof — furring strips — it said "directly over the prepared decking"
 * and, a few lines later, "install furring strips ... and fasten the new panels
 * through them": two contradictory promises about the same fasteners on one
 * page. And the sentences landed unnumbered between "3. Additional Notes" and
 * "4. Warranty", where they read as a note rather than as the work.
 *
 * So the answers go where the work is described. When the template has the
 * fastening sentence, the furring answer qualifies it in place and the finish
 * follows it inside 2.3 — the stored text is untouched and one sentence is read
 * two ways. When it has not (edited in Tools, or a template written later), the
 * answers close section 2, before the Additional Notes. With no sections at all
 * they follow whatever is there.
 */
function scopeWithAnswers(
  scope: string | null | undefined,
  answers: ExposedFastenerAnswers,
): string {
  const [finishLine, fasteningLine] = exposedFastenerScopeOfWork(answers) as [
    string,
    string,
  ];
  const text = (scope ?? "").replace(/\r\n/g, "\n").trim();
  if (text === "") return [finishLine, fasteningLine].join("\n\n");

  const lines = text.split("\n");

  const fastening = lines.findIndex((line) => DIRECT_TO_DECK.test(line));
  if (fastening !== -1) {
    if (answers.furringStrips) {
      lines[fastening] = lines[fastening]!.replace(
        DIRECT_TO_DECK,
        OVER_FURRING,
      );
    }
    lines.splice(fastening + 1, 0, finishLine);
    return lines.join("\n");
  }

  const notes = lines.findIndex((line) => SECTION_THREE.test(line));
  if (notes > 0) {
    let end = notes;
    while (end > 0 && lines[end - 1]!.trim() === "") end -= 1;
    lines.splice(end, 0, finishLine, fasteningLine);
    return lines.join("\n");
  }

  return [text, finishLine, fasteningLine].join("\n\n");
}

/**
 * The whole scope of work a metal panel estimate carries, for one finish.
 *
 * Jonathon, 2026-09-09: "if the rep selects galvalume then the estimate
 * presented to the customer does not need to say painted."
 *
 * Warning about the contradiction was not enough, and it was never going to be:
 * the warranty section of both metal scopes promises lifetime protection of a
 * *paint finish*, which on bare galvalume is not a wrong word but a commitment
 * nobody can honour. So the finish-dependent part is stored separately per
 * finish and only the matching one is written.
 *
 * Two pieces, in the order the customer reads them:
 *
 *   1. `scope` — sections 1 to 3, which are the same roof either way — with
 *      the answers written into section 2, where the installation is. See
 *      `scopeWithAnswers`. The answers are per-estimate and cannot live on
 *      the template.
 *   2. Section 4, the warranty for the finish that was chosen.
 *
 * A template with no scope at all (the R panel one, today) still gets the
 * answers, so the estimate says something rather than nothing.
 */
export function metalScopeNote(
  template: MetalScopeTemplate | undefined,
  answers: ExposedFastenerAnswers,
): string | null {
  const warranty =
    answers.finish === "galvalume"
      ? template?.scope_warranty_galvalume
      : template?.scope_warranty_painted;

  return (
    [scopeWithAnswers(template?.scope, answers), warranty ?? ""]
      .map((part) => (part ?? "").trim())
      .filter((part) => part !== "")
      .join("\n\n") || null
  );
}

/**
 * Anything worth saying out loud before the order goes out.
 *
 * One warning that matters: a galvalume roof whose order still has painted
 * parts on it. Shown rather than blocked, because the rep can price the job and
 * the office can correct four trim lines, and a hard stop on a Friday afternoon
 * helps nobody.
 *
 * `nameOf` names the parts, so the rep knows which lines to look at rather
 * than only how many.
 */
export function exposedFastenerWarnings(
  answers: ExposedFastenerAnswers,
  missing: readonly string[],
  templateScope?: string | null,
  nameOf?: (productId: string) => string | null | undefined,
): string[] {
  if (answers.finish !== "galvalume") return [];
  const warnings: string[] = [];

  if (missing.length > 0) {
    const names = [
      ...new Set(
        missing
          .map((id) => nameOf?.(id)?.trim())
          .filter((name): name is string => !!name),
      ),
    ];
    warnings.push(
      `${missing.length} ${
        missing.length === 1 ? "line has" : "lines have"
      } no galvalume version in the catalogue, so ${
        missing.length === 1 ? "it is" : "they are"
      } still painted on this material order${
        names.length > 0 ? `: ${names.join("; ")}` : ""
      }. Check the order before it is sent.`,
    );
  }

  /*
   * The finish-neutral part of the scope, against the finish being bought.
   *
   * `metalScopeNote` picks the warranty section by finish, so the promise a
   * galvalume roof carries is correct by construction. This guards what that
   * cannot reach: a sentence somebody types into sections 1 to 3 that says
   * painted anyway. Said out loud, and nothing is blocked — the rep edits the
   * scope on the Present screen.
   */
  if (scopeReadsPainted(templateScope)) {
    warnings.push(
      "This template's scope of work describes a painted roof and its paint " +
        "warranty. You have chosen galvalume — edit the scope on the Present " +
        "screen before the customer reads it.",
    );
  }

  return warnings;
}

/** Whether a scope commits the company to paint. Word-boundary, not substring. */
function scopeReadsPainted(scope: string | null | undefined): boolean {
  return /\b(painted|paint finish)\b/i.test(scope ?? "");
}
